import * as React from "react";
import { Text, View } from "react-native";
import { Link, useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";

import { PageMeta } from "~/components/PageMeta";
import { supabaseClient, useSessionContext } from "~/lib/supabase";
import { email, password } from "~/lib/validation";
import { Button } from "~/ui/Button";
import { Form } from "~/ui/Form";
import { TextInput } from "~/ui/TextInput";

export default function RecoveryPage() {
  const { session, isLoading } = useSessionContext();

  if (isLoading) return <Text>Loading...</Text>;
  return (
    <View className="container mb-32 max-w-96 flex-grow items-stretch justify-center gap-10 p-6">
      {session ? <SetPasswordForm /> : <SendInstructionsForm />}
    </View>
  );
}
const sendInstructionsSchema = z.object({
  email,
});

function SendInstructionsForm() {
  const [error, setError] = React.useState<string | undefined>();
  const [sent, setSent] = React.useState<boolean>(false);
  const form = useForm<z.infer<typeof sendInstructionsSchema>>({
    resolver: zodResolver(sendInstructionsSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(data: z.infer<typeof sendInstructionsSchema>) {
    setError(undefined);
    const { data: sbData, error } =
      await supabaseClient.auth.resetPasswordForEmail(data.email);
    if (error) {
      setError(error.message);
    } else {
      setSent(true);
    }
  }
  return (
    <>
      <PageMeta title="Forgot your password?" />
      <View className="items-center gap-2">
        <Text className="text-text-primary text-28 font-semibold">
          Forgot your password?
        </Text>
        <Text className="text-text-secondary text-center">
          No worries, it happens to the best of us.
        </Text>
      </View>
      <FormProvider {...form}>
        <Form onSubmit={form.handleSubmit(onSubmit)} className="gap-12">
          <Controller
            control={form.control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextInput
                autoFocus
                label="Account email"
                onChangeText={field.onChange}
                value={field.value}
                inputMode="email"
                errorMessage={error?.message}
              />
            )}
          />
          {sent && (
            <View className="-my-6 gap-2">
              <Text className="text-text-primary">
                If this email is registered, we have sent you instructions to
                reset your password.
              </Text>
              <Text className="text-text-primary">
                Please check your inbox and spam folder.
              </Text>
            </View>
          )}
          <View className="gap-4">
            <Button
              onPress={form.handleSubmit(onSubmit)}
              isLoading={form.formState.isSubmitting}
            >
              {sent ? "Not received? Send again!" : "Send reset instructions"}
            </Button>
            <Link href="/auth/login-with-email" asChild>
              <Button variant="secondary">Back to login</Button>
            </Link>
          </View>
        </Form>
      </FormProvider>
    </>
  );
}

const setPasswordSchema = z.object({
  password,
});

function SetPasswordForm() {
  const [formError, setError] = React.useState<string | undefined>();
  const { replace } = useRouter();
  const form = useForm<z.infer<typeof setPasswordSchema>>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: "",
    },
  });

  async function onSubmit(data: z.infer<typeof setPasswordSchema>) {
    setError(undefined);
    const { data: sbData, error } = await supabaseClient.auth.updateUser({
      password: data.password,
    });
    if (error) {
      console.error(error);
      setError(error.message);
    } else {
      replace("/");
    }
  }

  return (
    <>
      <View className="items-center gap-2">
        <Text className="text-text-primary text-28 font-semibold">
          Set your new password.
        </Text>
      </View>
      <FormProvider {...form}>
        <Form onSubmit={form.handleSubmit(onSubmit)}>
          <View className="gap-8">
            <Controller
              control={form.control}
              name="password"
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  label="Set your password"
                  placeholder="Password123"
                  autoFocus
                  onChangeText={field.onChange}
                  value={field.value}
                  secureTextEntry
                  autoCapitalize="none"
                  autoComplete="off"
                  inputMode="text"
                  textContentType="newPassword"
                  passwordRules="minlength: 8"
                  errorMessage={error?.message || formError}
                  autoCorrect={false}
                />
              )}
            />
            <Button
              onPress={form.handleSubmit(onSubmit)}
              isLoading={form.formState.isSubmitting}
            >
              Set new password
            </Button>
          </View>
        </Form>
      </FormProvider>
    </>
  );
}
